import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Block, Build } from '@mui/icons-material'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import { useGetTrackedGameLiveEventBlueprintsQuery } from '../../../../../api/core'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { endOfDayTimestamp } from '../../../../../helpers/endOfDayTimestamp'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { useCurrentMarket } from '../../../../markets'
import { useTrackedEvent } from '../../../hooks/useTrackedEvent'
import { useTrackedEvents } from '../../../hooks/useTrackedEvents'
import { TrackedGame } from '../../../types/TrackedGame'
import { TrackingEvent } from '../../../types/TrackingEvents'
import { LiveEventEvolutionTimeline } from '../../LiveEventEvolutionTimeline/LiveEventEvolutionTimeline'
import {
  countMechanicChangeEntries,
  filterStartedEntries,
  getCommonNamePrefix,
  hasMechanicChange,
  LiveEventEvolutionScope,
} from '../../helpers/liveEventEvolutionTimelineHelpers'
import './EvolutionTab.scss'

type EvolutionTabProps = {
  currentTrackedGame?: TrackedGame
  currentEventId?: string
  onEventSelect: (event: TrackingEvent) => void
  /** Opens the Instances tab of the clicked version's event; forwarded to the timeline's "See instances" action. */
  onOpenInstancesTab?: (eventId: string, event?: TrackingEvent) => void
  /** Scope the tab opens on; every event instance ("All") when omitted. An explicit scope from the URL wins. */
  initialScope?: LiveEventEvolutionScope
}

type SegmentToggle = LiveEventEvolutionScope

export const EvolutionTab: FC<EvolutionTabProps> = ({ currentEventId, currentTrackedGame, onEventSelect, onOpenInstancesTab, initialScope = 'all' }) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const [segmentToggle, setSegmentToggle] = useState<SegmentToggle>(initialScope)
  const { currentMarketIso } = useCurrentMarket()
  const [showOnlyMechanicChanges, setShowOnlyMechanicChanges] = useState(false)
  // The timeline scrolls inside its own box while the dialog around it does
  // not, so a "back to top" on page change has to move this box rather than the
  // dialog's content.
  const timelineScrollerRef = useRef<HTMLDivElement>(null)
  const handleScrollTop = useCallback(() => {
    timelineScrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])
  const { data: eventData, isLoading: isLoadingEventData, isFetching: isFetchingEventData } = useTrackedEvent(currentEventId)
  const blueprintId = eventData?.event.blueprintEvolutionData?.blueprintId
  const {
    data: liveEventCollection,
    isLoading: isLoadingLiveEventCollection,
    isFetching: isFetchingLiveEventCollection,
  } = useGetTrackedGameLiveEventBlueprintsQuery(
    {
      blueprintId: blueprintId || '',
      type: segmentToggle,
    },
    {
      skip: !blueprintId || !segmentToggle,
    }
  )
  const gameIds = useMemo(() => (currentTrackedGame ? [currentTrackedGame?.game.id] : []), [currentTrackedGame])
  const trackedEventsQuery = useTrackedEvents({ gameIds: gameIds, startTimestamp: 0, endTimestamp: endOfDayTimestamp(), marketIso: currentMarketIso })

  const filteredEvents = useMemo(() => {
    const events = trackedEventsQuery.data?.events || {}
    const uniqueEventsMap: { [eventId: string]: TrackingEvent } = {}

    if (!liveEventCollection) {
      return uniqueEventsMap
    }

    const evolutionEventIds = liveEventCollection.evolution?.map((evolution) => evolution.eventId) || []

    if (liveEventCollection.evolution?.length === 0) {
      return uniqueEventsMap
    }

    Object.values(events).forEach((gameEvents) => {
      gameEvents.forEach((event) => {
        if (evolutionEventIds.includes(event.eventId)) {
          uniqueEventsMap[event.eventId] = event
          // also index by eventId+start so the evolution timeline can look up the specific instance
          uniqueEventsMap[`${event.eventId}-${event.start}`] = event
        }
      })
    })

    return uniqueEventsMap
  }, [liveEventCollection, trackedEventsQuery.data?.events])

  const allEvolutionEntries = useMemo(() => liveEventCollection?.evolution || [], [liveEventCollection?.evolution])

  // The timeline never lists a scheduled version or instance, so the summary, the empty states and the rows all work from
  // the started entries. A blueprint whose entries are all still scheduled then reads as having no data, not as "0 versions".
  // Under "All" every entry is one event instance, which is what Live Event Analytics counts for the same blueprint.
  const startedEvolutionEntries = useMemo(() => filterStartedEntries(allEvolutionEntries, Date.now()), [allEvolutionEntries])

  const filteredEvolutionEntries = useMemo(
    () => (showOnlyMechanicChanges ? startedEvolutionEntries.filter((entry) => hasMechanicChange(entry, segmentToggle)) : startedEvolutionEntries),
    [startedEvolutionEntries, showOnlyMechanicChanges, segmentToggle]
  )

  const blueprintName = useMemo(() => {
    const blueprint = currentTrackedGame?.blueprints?.find((blueprint) => blueprint.blueprintId === blueprintId)
    if (!blueprint?.names) return undefined
    return blueprint.names[userLanguage] || blueprint.names.en
  }, [currentTrackedGame, blueprintId, userLanguage])

  // Rows drop the prefix shared by every version name only when the blueprint title already says it; otherwise they keep
  // their full names so the prefix is never lost. Computed from all entries so the "mechanic changes only" toggle can't change row names.
  const commonNamePrefix = useMemo(() => getCommonNamePrefix(allEvolutionEntries.map((entry) => entry.name)), [allEvolutionEntries])
  // The shared prefix ends with its separator ("Name: "), which a title that is just the name wouldn't have.
  const prefixName = commonNamePrefix.replace(/[\s\-–—:]+$/, '')
  const rowNamePrefix = prefixName && blueprintName?.startsWith(prefixName) ? commonNamePrefix : ''

  // The summary describes the whole scope, so it must not shrink when the toggle hides versions.
  const mechanicChangeCount = useMemo(() => countMechanicChangeEntries(startedEvolutionEntries, segmentToggle), [startedEvolutionEntries, segmentToggle])

  const timelineEmptyMessage =
    showOnlyMechanicChanges && startedEvolutionEntries.length > 0 && filteredEvolutionEntries.length === 0
      ? t('live-events:live_event_evolution_no_mechanic_changes')
      : undefined

  const isLoading = isLoadingEventData || isLoadingLiveEventCollection || isFetchingLiveEventCollection || isFetchingEventData

  const isDiscontinued = useMemo(() => {
    return currentTrackedGame?.blueprints?.find((blueprint) => blueprint.blueprintId === blueprintId)?.discontinued
  }, [blueprintId, currentTrackedGame?.blueprints])

  const handleSegmentToggle = (event: React.MouseEvent<HTMLElement>, value: SegmentToggle) => {
    if (value !== null) {
      setSegmentToggle(value)
    }
  }

  const showTools = !isLoading && Boolean(liveEventCollection?.evolution) && Boolean(currentTrackedGame)

  return (
    <Box className="EvolutionTab">
      <Box className="EvolutionTabHeader">
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {isDiscontinued && (
              <GRTooltip content={t('live-events:live_event_evolution_type_discontinued')} styles={{ height: '20px', display: 'inline-flex' }}>
                <Block color="error" fontSize="small" />
              </GRTooltip>
            )}
            <Typography component="h3" sx={{ fontSize: '20px', fontWeight: 'bold', overflowWrap: 'anywhere' }}>
              {blueprintName}
            </Typography>
          </Box>
          {showTools && startedEvolutionEntries.length > 0 && (
            <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mt: 0.25 }}>
              {t(segmentToggle === 'all' ? 'live-events:live_event_evolution_instances_summary' : 'live-events:live_event_evolution_versions_summary', {
                count: startedEvolutionEntries.length,
                mechanicCount: mechanicChangeCount,
              })}
            </Typography>
          )}
        </Box>

        {showTools && (
          <Box className="ToolsContainer">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GRTooltip content={t('live-events:show_only_mechanic_changes')}>
                <ToggleButton
                  value="check"
                  className="MechanicChangesToggle"
                  size="small"
                  selected={showOnlyMechanicChanges}
                  onClick={() => setShowOnlyMechanicChanges(!showOnlyMechanicChanges)}
                >
                  <Build fontSize="small" sx={{ mr: 0.75 }} />
                  {t('live-events:live_event_evolution_mechanic_changes_only')}
                </ToggleButton>
              </GRTooltip>
              <ToggleButtonGroup className="SegmentToggle" value={segmentToggle} exclusive onChange={handleSegmentToggle} size="small">
                <GRTooltip content={<Trans i18nKey="live-events:show_by_all_events_tooltip" />}>
                  <ToggleButton value="all">
                    <Trans i18nKey="common:all" />
                  </ToggleButton>
                </GRTooltip>
                <GRTooltip content={<Trans i18nKey="live-events:show_by_first_event_tooltip" />}>
                  <ToggleButton value="first">
                    <Trans i18nKey="common:first" />
                  </ToggleButton>
                </GRTooltip>
                <GRTooltip content={<Trans i18nKey="live-events:show_by_last_event_tooltip" />}>
                  <ToggleButton value="last">
                    <Trans i18nKey="common:latest" />
                  </ToggleButton>
                </GRTooltip>
              </ToggleButtonGroup>
            </Box>
          </Box>
        )}
      </Box>

      <Box ref={timelineScrollerRef} className="EvolutionTabScroller">
        <LiveEventEvolutionTimeline
          loading={isLoading}
          entries={filteredEvolutionEntries}
          scope={segmentToggle}
          onEventSelect={onEventSelect}
          events={filteredEvents as { [eventId: string]: TrackingEvent }}
          currentEventId={currentEventId}
          currentTrackedGame={currentTrackedGame}
          onScrollParentTop={handleScrollTop}
          namePrefix={rowNamePrefix}
          onOpenInstancesTab={onOpenInstancesTab}
          onShowAllVersions={segmentToggle === 'all' ? undefined : () => setSegmentToggle('all')}
          emptyMessage={timelineEmptyMessage}
        />
      </Box>
    </Box>
  )
}
